import { clsx, type ClassValue } from "clsx";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import pluralize from "pluralize";

import type { AuthUser } from "src/types/auth";

type ToastOptions = Parameters<typeof toast.success>[1];

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export const getFromLocalStorage = <T,>(key: string, defaultValue?: T): T | undefined => {
	try {
		const value = localStorage.getItem(key);
		return value ? (JSON.parse(value) as T) : defaultValue;
	} catch (error) {
		console.error(error);
		return undefined;
	}
};

export const setToLocalStorage = (key: string, value: unknown) => {
	try {
		localStorage.setItem(key, JSON.stringify(value));
	} catch (error) {
		console.error(`Error saving "${key}":`, error);
	}
};

export const removeFromLocalStorage = (...keys: string[]) => keys.forEach((key) => localStorage.removeItem(key));

export const clearLocalStorage = () => {
	try {
		localStorage.clear();
	} catch (error) {
		console.error(`Error clearing local storage:`, error);
	}
};

export const showSuccessToast = (message: string, data?: ToastOptions) =>
	toast.success(message, {
		...data,
	});

export const showErrorToast = (message: string, data?: ToastOptions) =>
	toast.error(message, {
		...data,
	});

export const showInfoToast = (message: string, data?: ToastOptions) =>
	toast.info(message, {
		...data,
	});

export const showLoadingToast = (message: string, data?: ToastOptions) => toast.loading(message, data);

export const showWarningToast = (message: string, data?: ToastOptions) =>
	toast.warning(message, {
		...data,
	});

interface PromiseToastOptions {
	loading?: string;
	success?: unknown;
	error?: unknown;
	[key: string]: unknown;
}

export const showPromiseToast = <T,>(promise: Promise<T>, { loading, success, error, ...rest }: PromiseToastOptions) =>
	toast.promise(promise, {
		loading,
		success,
		error,
		duration: 2000,
		...rest,
	} as Parameters<typeof toast.promise>[1]);

export function removeEmptyOrNull<T extends Record<string, unknown>>(obj: T | null | undefined) {
	if (!obj) return undefined;

	const filteredEntries = Object.entries(obj).filter(([, value]) => value !== "" && value !== null);

	return filteredEntries.length > 0 ? Object.fromEntries(filteredEntries) : {};
}

export const renderCurrency = (amount: number | string = 0, currency = "NGN") =>
	new Intl.NumberFormat("en-GB", {
		style: "currency",
		currency,
		currencyDisplay: "narrowSymbol",
		minimumFractionDigits: Number(amount) % 1 === 0 ? 0 : 2,
		maximumFractionDigits: 2,
	}).format(parseFloat(amount.toString()) || 0);

export const renderNumber = (amount: number | string = 0) =>
	new Intl.NumberFormat("en-GB", {
		currencyDisplay: "narrowSymbol",
		minimumFractionDigits: Number(amount) % 1 === 0 ? 0 : 2,
		maximumFractionDigits: 2,
	}).format(parseFloat(amount.toString()) || 0);

export const isSpanElement = (element: unknown): element is HTMLSpanElement => element instanceof HTMLSpanElement;

export const copyToClipboard = async (text: string) => {
	if ("clipboard" in navigator) {
		return Promise.resolve(navigator.clipboard.writeText(text));
	} else {
		return Promise.resolve(document.execCommand("copy", true, text));
	}
};

function capitalizeFirstLetter(string: string) {
	if (string.length === 0) {
		return ""; // Handle empty strings gracefully
	}
	return string.charAt(0).toUpperCase() + string.slice(1);
}

/**
 * Reads a tenant's label override (e.g. "customer_label" -> "Client").
 *
 * Returns "" when the tenant has no override for `label`. The JavaScript version
 * passed `undefined` straight into `capitalizeFirstLetter`/`pluralize`, which threw;
 * callers should supply their own fallback (see `navConfig.tsx`).
 */
export const getCustomizationValue = (user: AuthUser | null | undefined, label: string, returnSingular = false): string => {
	const value = user?.business_customization?.find(({ label: customizationLabel }) => customizationLabel === label)?.value;

	if (!value) return "";

	if (returnSingular) return capitalizeFirstLetter(value);

	return capitalizeFirstLetter(pluralize.plural(value));
};

export interface MonthlyDatum {
	month: string;
	invoices: number;
	payments: number;
}

export function toChartSeries(monthlyData: MonthlyDatum[]) {
	return [
		{
			label: "Invoices",
			data: monthlyData.map((item) => ({
				primary: item.month,
				secondary: item.invoices,
			})),
		},
		{
			label: "Payments",
			data: monthlyData.map((item) => ({
				primary: item.month,
				secondary: item.payments,
			})),
		},
	];
}
